import React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Wrapper, useStyles } from "./style";

type RowData = {
  id: string;
  fullName: string;
  email: string;
  role: string;
  [key: string]: any;
};

type BaseDataTableProps = {
  data: RowData[];
  columns?: GridColDef[];
  option: (event: { action: string; id: string; row: RowData }) => void;
  onMenuOptionClick?: (action: string, id: string, row: RowData) => void;
  loading?: boolean;
  page?: number;
  pageSize?: number;
  rowsPerPageOptions?: number[];
  hideFooter?: boolean;
  autoHeight?: boolean;
  wrapperAutoHeight?: boolean;
  rowCount?: number;
  paginationMode?: "client" | "server";
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  selectionModel?: any[];
  onSelectionModelChange?: (selectionModel: any[]) => void;
  checkboxSelection?: boolean;
  disableSelectionOnClick?: boolean;
  filterMode?: "client" | "server";
  onFilterModelChange?: (filterModel: any) => void;
  onColumnHeaderClick?: (params: any) => void;
  onRowClick?: (params: any) => void;
  getRowClassName?: (params: any) => string;
  components?: any;
  componentsProps?: any;
  // rowsPerPageOptions?: number[];
  // pageSize?: number;
};

export const BaseDataTable = (props: BaseDataTableProps) => {
  const classes = useStyles();
  // `Wrapper` is defined in a JS styled-component file; cast to allow transient props.
  const WrapperAny = Wrapper as unknown as React.ComponentType<any>;
  const wrapperAutoHeight = props.wrapperAutoHeight ?? props.autoHeight;

  return (
    <WrapperAny $autoHeight={wrapperAutoHeight}>
      <DataGrid
        rows={props.data}
        columns={props.columns || []}
        loading={props.loading}
        components={props.components}
        componentsProps={props.componentsProps}
        rowCount={props.rowCount}
        pagination
        paginationMode={props.paginationMode}
        page={props.page}
        onPageChange={props.onPageChange}
        onPageSizeChange={props.onPageSizeChange}
        pageSize={props.pageSize ?? 50}
        hideFooterPagination={false}
        hideFooterSelectedRowCount
        checkboxSelection={props.checkboxSelection}
        disableSelectionOnClick={props.disableSelectionOnClick}
        filterMode={props.filterMode}
        onFilterModelChange={props.onFilterModelChange}
        onColumnHeaderClick={props.onColumnHeaderClick}
        onRowClick={props.onRowClick}
        getRowClassName={props.getRowClassName ?? (() => `MuiTableCell-row`)}
        rowsPerPageOptions={props.rowsPerPageOptions ?? [25, 50, 100]}
        hideFooter={props.hideFooter}
        autoHeight={props.autoHeight}
        rowHeight={52}
        headerHeight={44}
        sortingOrder={["asc", "desc"]}
        getCellClassName={() => classes.cell}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
        sx={{
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell": {
            paddingInline: "8px !important",
            border: 0,
            paddingBlock: "8px !important",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "none",
          },
          // Even row background color
          "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row:nth-of-type(even)":
            {
              backgroundColor: `#f9f9f9 !important`,
            },
          "& .MuiDataGrid-row": {
            borderRadius: 0,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F9FAFB !important",
            color: "#111827 !important",
            borderBottom: "1px solid #E5E7EB !important",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          // Keep pagination visible even on short viewports
          "& .MuiDataGrid-footerContainer": {
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            backgroundColor: "#fff",
            borderTop: "1px solid #E5E7EB",
          },
          // Ensure last row isn't hidden behind sticky footer
          "& .MuiDataGrid-virtualScroller": {
            paddingBottom: "56px",
          },
          // Hover should override striped (even-row) background too.
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f0f8ff !important",
            border: "0 !important",
          },
          "& .MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row:nth-of-type(even):hover":
            {
              backgroundColor: "#f0f8ff !important",
            },
          // Selected row state (no weird rounding, subtle accent)
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "rgba(0, 118, 255, 0.08) !important",
            boxShadow: "inset 3px 0 0 0 rgba(0, 118, 255, 0.9)",
          },
          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "rgba(0, 118, 255, 0.12) !important",
          },

          backgroundColor: "#fff",
          height: "100%",
        }}
      />
    </WrapperAny>
  );
};
